import React, {
  useEffect,
  useState,
} from 'react';
import { DateTime } from 'luxon';

import getBarXCoords from './utils/getBarXCoords';

// TODO: Arrow start markers, to make it easy to see which deps are
//   cutting through vs beginning
// TODO: Link symbol

const GanttArrows = ({
  dateMode,
  colOffset,
  colCount,
  colSize,
  arrowBodyStyle,
  arrowBodyDashArray = "4",
  arrowHeadStyle,
  arrowHeadDashArray = "0",
  barOptions,
  barData,
}) => {
  const [arrows, setArrows] = useState([]);
  const defaultArrowStyle = {
    fill: 'none',
    stroke: '#666',
    strokeWidth: '1.4',
  };
  
  const getArrow = (
    barDatum, 
    depFromIdx, 
    depToIdx, 
    arrowBodyStyle = defaultArrowStyle,
    arrowBodyDashArray = "0",
    arrowHeadStyle = defaultArrowStyle,
    arrowHeadDashArray = "0",
  ) => {
    const minLen = 5;
    const r = 5;
    const head = 5;
    
    const { barHeight } = barOptions;
    const halfBarH = barHeight / 2;
    const barYSpace = (colSize - barHeight);
  
    const depFrom = barDatum;
    const depTo = barData[depToIdx];
    
    const depsBetween = barData.slice(depFromIdx + 1, depToIdx);
    
    // Verticals
    const fromMidY = colSize * (depFromIdx + 0.5);
    const toMidY = colSize * (depToIdx + 0.5);
    
    const fromBottomY = fromMidY + halfBarH;
    const toBottomY = toMidY + halfBarH;
    
    const fromTopY = fromMidY - halfBarH;
    const toTopY = toMidY - halfBarH;
    
    const dY = toMidY - fromMidY;
    
    // Horizontals
    const getCurrBarXCoords = bar => getBarXCoords(bar, dateMode, colSize, colOffset);
    
    const fromX = getCurrBarXCoords(depFrom);
    const toX = getCurrBarXCoords(depTo);
    
    const fromMidX = (fromX.start + fromX.end) / 2;
    const toMidX = (toX.start + toX.end) / 2;
    
    const fromBarLen = Math.abs(fromX.end - fromX.start);
    const toBarLen = Math.abs(toX.end - toX.start);
    
    const dX = toMidX - fromMidX;
    
    // Sign dependent
    const negDY = dY < 0;
    const absDY = Math.abs(dY);
    const signY = negDY ? -1 : 1;
    
    const negDX = dX < 0;
    const absDX = Math.abs(dX);
    const signX = negDX ? -1 : 1;
    
    const dShortestX = dX - signX * (fromBarLen + toBarLen) / 2;
    const dShortestY = dY - signY * barHeight;
    
    // Corners
    const corners = {
      NE: (r) => `a ${r} ${r} 0 0 1 ${r} ${r}`,
      ES: (r) => `a ${r} ${r} 0 0 1 -${r} ${r}`,
      SW: (r) => `a ${r} ${r} 0 0 1 -${r} -${r}`,
      WN: (r) => `a ${r} ${r} 0 0 1 ${r} -${r}`,
      NW: (r) => `a ${r} ${r} 0 0 0 -${r} ${r}`,
      WS: (r) => `a ${r} ${r} 0 0 0 ${r} ${r}`,
      SE: (r) => `a ${r} ${r} 0 0 0 ${r} -${r}`,
      EN: (r) => `a ${r} ${r} 0 0 0 -${r} -${r}`,
      getType: (dX, dY, hv) => {
        if (hv) {
          const dirH = `${dX < 0 ? 'W' : 'E'}`;
          const dirV = `${dY < 0 ? 'S' : 'N'}`;
          
          return `${dirV}${dirH}`;
        } else {
          const dirH = `${dX < 0 ? 'E' : 'W'}`;
          const dirV = `${dY < 0 ? 'N' : 'S'}`;
          
          return `${dirH}${dirV}`;
        }
      },
      getCorner: function (dX, dY, hv, r) {
        return this[this.getType(dX, dY, hv)](r);
      },
    };
    // Arrow heads
    const heads = {
      u: `m -${head} ${head} l ${head} -${head} l ${head} ${head}`,
      d: `m ${head} -${head} l -${head} ${head} l -${head} -${head}`,
      l: `m ${head} -${head} l -${head} ${head} l ${head} ${head}`,
      r: `m -${head} -${head} l ${head} ${head} l -${head} ${head}`,
      getTypeY: (dY) => dY < 0 ? 'u' : 'd',
      getTypeX: (dX) => dX < 0 ? 'l' : 'r',
      getY: function(dY) {
        return this[this.getTypeY(dY)];
      },
      getX: function (dX) {
        return this[this.getTypeX(dX)];
      },
    };
    
    // Conditions
    const endToEnd = (toBarLen + fromBarLen) / 2 + (minLen + r) * 2;
    const midToEnd = toBarLen / 2 + (minLen + r);
    
    const isEndToEnd = endToEnd < absDX;
    const isMidToEnd = midToEnd < absDX;
    const isMidToMid = absDX < midToEnd;
    
    // Get intersecting deps
    const getIxDeps = (xPos, startIdx, endIdx) => {
      const ixDeps = [];
      const absStartIdx = startIdx < endIdx ? startIdx : endIdx;
      const absEndIdx = startIdx > endIdx ? startIdx : endIdx;
      
      for (let i = absStartIdx + 1; i < absEndIdx; i++) {
        const barDatum = barData[i];
        const { start, end } = getBarXCoords(barDatum, dateMode, colSize, colOffset);
        
        if (start < xPos && end > xPos) ixDeps.push(i);
      }
      
      return ixDeps;
    }
    
    const getCurrIxDeps = (xPos) => getIxDeps(xPos, depToIdx, depFromIdx);
    
    // Paths for each case
    let arrowPath;
    let headPath;
    
    if (isEndToEnd) {
      const startX = negDX ? fromX.start : fromX.end;
      const start = `M ${startX} ${fromMidY}`;
      
      const endX = negDX ? toX.end : toX.start;
      const end = `M ${endX} ${toMidY}`;
      
      const horz = `h ${signX * minLen}`;
      const cA = corners.getCorner(dX, dY, true, r);
      
      const vert = `v ${signY * (absDY - 2 * r)}`;
      const cB = corners.getCorner(dX, dY, false, r);
      
      const horzB = `h ${dShortestX - signX * (r * 2 + minLen)}`;
      
      const head = heads.getX(dX);
      
      const vertX = startX + signX * (minLen + r);
      
      const ixDeps = getCurrIxDeps(vertX);
      console.log('ix', ixDeps);
      
      arrowPath = `${start} ${horz} ${cA} ${vert} ${cB} ${horzB}`;
      headPath = `${end} ${head}`;
    }
    else if (isMidToEnd) {
      const startY = negDY ? fromTopY : fromBottomY;
      const start = `M ${fromMidX} ${startY}`;
      
      const endX = negDX ? toX.end : toX.start;
      const end = `M ${endX} ${toMidY}`;
      
      const vert = `v ${signY * (absDY - (halfBarH + r))}`;
      
      const horzToX = negDX ? toX.end : toX.start;
      const horz = `h ${horzToX - fromMidX - signX * r}`
      
      const cA = corners.getCorner(dX, dY, false, r);
      
      const head = heads.getX(dX);
      
      const vertX = fromMidX;
      
      const ixDeps = getCurrIxDeps(vertX);
      ixDeps.map((dep) => {
        
      });
      
      arrowPath = `${start} ${vert} ${cA} ${horz}`;
      headPath = `${end} ${head}`;
    } 
    else if (isMidToMid) {
      const currR = Math.min(r, Math.abs(dX) / 2);
      
      const startY = negDY ? fromTopY : fromBottomY;
      const start = `M ${fromMidX} ${startY}`;
      
      const endY = negDY ? toBottomY : toTopY;
      const end = `M ${toMidX} ${endY}`;
      
      const cA = corners.getCorner(dX, dY, false, currR);
      const cB = corners.getCorner(dX, dY, true, currR);
      
      const vert = `v ${(dShortestY / 2) - signY * currR}`;
      
      const horzLen = Math.max(Math.abs(dX - (signX * 2 * currR)), 0);
      const horz = `h ${signX * horzLen}`;
      
      const head = heads.getY(dY);
      
      const vertAX = fromMidX;
      const vertBX = fromMidX + signX * (2 * currR + horzLen);
      
      const ixFirstDeps = getCurrIxDeps(vertAX);
      const ixSecondDeps = getCurrIxDeps(vertBX);
      console.log('ix', ixFirstDeps, ixSecondDeps);
      
      arrowPath = `${start} ${vert} ${cA} ${horz} ${cB} ${vert}`;
      headPath = `${end} ${head}`;
    }
    
    return (
      <React.Fragment key={`${depFromIdx}-${depToIdx}`}>
        <path
          key={`${depFromIdx}-${depToIdx}-body`}
          d={arrowPath}
          style={arrowBodyStyle}
          strokeDasharray={arrowBodyDashArray}
        />
        <path
          key={`${depFromIdx}-${depToIdx}-head`}
          d={headPath}
          style={arrowHeadStyle}
          strokeDasharray={arrowHeadDashArray}
        />
      </React.Fragment>
    );
  };
  
  useEffect(() => {
    const updatedArrows = [];
    for (let fromIdx = 0; fromIdx < barData.length; fromIdx++) {
      let barDatum = barData[fromIdx];
      if (!barDatum || !barDatum.deps) continue
      
      for (let toIdx = 0; toIdx < barDatum.deps.length; toIdx++) {
        updatedArrows.push(getArrow(barDatum, fromIdx, barDatum.deps[toIdx]));
      }
    }
    
    setArrows(updatedArrows);
  }, [barData, colOffset, dateMode]);
  
  // useEffect(() => console.log(arrows), [arrows]);
  
  // // console.log('colCount', colCount);
  // // console.log('colOffset', colOffset);
  
  return (
    <svg
      id="arrows"
      width={colCount * colSize}
      height={barData.length * colSize}
      x="0"
      y={colSize * 2}
    >
      {arrows}
    </svg>
  );
};

export default GanttArrows;
